import React from 'react';
import {
  Box,
  Stack,
  Cluster,
  Text,
  constants,
  BankIcon,
  CheckIcon,
  GenericCard,
  CashIcon,
} from '@thecb/components';
import { formatPrice, titleCase } from '../../utils/helpers';

export const OrderDetailsPaymentItem = ({
  transactionData = {},
  index,
  isRefund,
}) => {
  const {
    amount_processed,
    gateway_payment_id: payment_id,
    status,
    change_due,
    payment_type = '',
    bank_account = {},
    credit_card = {},
  } = transactionData;

  const BankPayment = (
    <Cluster align="center" childGap="1rem">
      <BankIcon />
      {`Bank account ending in ${
        bank_account?.account_number_last_four ?? '****'
      }`}
    </Cluster>
  );
  const CardPayment = (
    <Cluster align="center" childGap="1rem">
      <GenericCard />
      {`Card ending in ${credit_card?.last_four ?? '****'}`}
    </Cluster>
  );
  const CashPayment = (
    <Cluster align="center" childGap="1rem">
      <CashIcon />
      {`Cash`}
    </Cluster>
  );
  const WirePayment = (
    <Cluster align="center" childGap="1rem">
      <CheckIcon />
      {`Wire Transfer`}
    </Cluster>
  );
  const PaymentType = () => {
    switch (payment_type) {
      case 'Check Payment':
        return BankPayment;
      case 'Card Payment':
        return CardPayment;
      // Need new icons for the other two types
      case 'Advice of Credit':
        return BankPayment;
      case 'Cash Payment':
        return CashPayment;
      case 'Wire Payment':
        return WirePayment;
      default:
        return BankPayment;
    }
  };
  return (
    <Box padding="1.5rem">
      <Stack childGap=".5rem">
        <Cluster direction="row" childGap="2rem">
          <Text
            fontSize="1rem"
            weight={constants.fontWeights.FONT_WEIGHT_BOLD}
            extraStyles={`width: 17.143rem;`}
          >
            Payment ID
          </Text>
          <Text fontSize="1rem" extraStyles={`flex-grow: 1;`}>
            {payment_id}
          </Text>
        </Cluster>
        <Cluster direction="row" childGap="2rem">
          <Text
            fontSize="1rem"
            weight={constants.fontWeights.FONT_WEIGHT_BOLD}
            extraStyles={`width: 17.143rem;`}
          >
            Payment Method
          </Text>
          <Text fontSize="1rem" extraStyles={`flex-grow: 1;`}>
            <PaymentType />
          </Text>
        </Cluster>
        {transactionData.payer_name && (
          <Cluster direction="row" childGap="2rem">
            <Text
              fontSize="1rem"
              weight={constants.fontWeights.FONT_WEIGHT_BOLD}
              extraStyles={`width: 17.143rem;`}
            >
              Payer Name
            </Text>
            <Text
              fontSize="1rem"
              themeValues={{ fontSize: '1rem' }}
              extraStyles={`flex-grow: 1;`}
            >
              {transactionData.payer_name}
            </Text>
          </Cluster>
        )}
        <Cluster direction="row" childGap="2rem">
          <Text
            fontSize="1rem"
            themeValues={{ fontSize: '1rem' }}
            weight={constants.fontWeights.FONT_WEIGHT_BOLD}
            extraStyles={`width: 17.143rem;`}
          >
            {payment_type === 'Cash' ? 'Amount Given' : 'Amount'}
          </Text>
          <Text
            fontSize="1rem"
            extraStyles={`flex-grow: 1;`}
            id={`payment-details-${index}-amount-processed`}
          >
            {formatPrice(amount_processed, isRefund)}
          </Text>
        </Cluster>
        {!!change_due && (
          <Cluster direction="row" childGap="2rem">
            <Text
              fontSize="1rem"
              weight={constants.fontWeights.FONT_WEIGHT_BOLD}
              extraStyles={`width: 17.143rem;`}
            >
              Change Given
            </Text>
            <Text
              fontSize="1rem"
              extraStyles={`flex-grow: 1;`}
              id={`payment-details-${index}-change-given`}
            >
              {formatPrice(change_due)}
            </Text>
          </Cluster>
        )}
        {!!transactionData.service_fee && (
          <Cluster direction="row" childGap="2rem">
            <Text
              fontSize="1rem"
              themeValues={{ fontSize: '1rem' }}
              weight={constants.fontWeights.FONT_WEIGHT_BOLD}
              extraStyles={`width: 17.143rem;`}
            >
              Service Fee
            </Text>
            <Text fontSize="1rem" extraStyles={`flex-grow: 1;`}>
              {formatPrice(transactionData.service_fee)}
            </Text>
          </Cluster>
        )}
        {!!transactionData.convenience_fee && (
          <Cluster direction="row" childGap="2rem">
            <Text
              fontSize="1rem"
              themeValues={{ fontSize: '1rem' }}
              weight={constants.fontWeights.FONT_WEIGHT_BOLD}
              extraStyles={`width: 17.143rem;`}
            >
              Transaction Fee
            </Text>
            <Text fontSize="1rem" extraStyles={`flex-grow: 1;`}>
              {formatPrice(transactionData.convenience_fee)}
            </Text>
          </Cluster>
        )}
        {!!transactionData.wire_payment?.name && (
          <Cluster direction="row" childGap="2rem">
            <Text
              fontSize="1rem"
              themeValues={{ fontSize: '1rem' }}
              weight={constants.fontWeights.FONT_WEIGHT_BOLD}
              extraStyles={`width: 17.143rem;`}
            >
              Name
            </Text>
            <Text fontSize="1rem" extraStyles={`flex-grow: 1;`}>
              {transactionData?.wire_payment?.name}
            </Text>
          </Cluster>
        )}
        {!!transactionData.wire_payment?.effective_date && (
          <Cluster direction="row" childGap="2rem">
            <Text
              fontSize="1rem"
              themeValues={{ fontSize: '1rem' }}
              weight={constants.fontWeights.FONT_WEIGHT_BOLD}
              extraStyles={`width: 17.143rem;`}
            >
              Effective Date
            </Text>
            <Text fontSize="1rem" extraStyles={`flex-grow: 1;`}>
              {(() => {
                const [year, month, day] =
                  transactionData.wire_payment.effective_date.split('-');
                return `${month}/${day}/${year}`;
              })()}
            </Text>
          </Cluster>
        )}
        <Cluster direction="row" childGap="2rem">
          <Text
            fontSize="1rem"
            weight={constants.fontWeights.FONT_WEIGHT_BOLD}
            extraStyles={`width: 17.143rem;`}
          >
            Status
          </Text>
          <Text
            fontSize="1rem"
            extraStyles={`flex-grow: 1;`}
            id={`payment-details-${index}-status`}
          >
            {titleCase(status)}
          </Text>
        </Cluster>
        {!!transactionData.wire_payment?.description && (
          <Cluster direction="row" childGap="2rem" nowrap>
            <Text
              fontSize="1rem"
              themeValues={{ fontSize: '1rem' }}
              weight={constants.fontWeights.FONT_WEIGHT_BOLD}
              extraStyles={`width: 17.143rem; flex-shrink: 0;`}
            >
              Description
            </Text>
            <Text fontSize="1rem" extraStyles={`flex-grow: 0;`}>
              {transactionData?.wire_payment?.description}
            </Text>
          </Cluster>
        )}
      </Stack>
    </Box>
  );
};
